import { FinalishApiError, FinalishClient } from './client';
import type {
  FinalishDockSide,
  FinalishHostIdentity,
  FinalishLauncherOptions,
  FinalishWidgetSession,
  FinalishWidgetSessionIdentityInput,
} from './types';
import { FinalishWidget } from './widget';

type WidgetConfig = {
  enabled: boolean;
};

type LauncherState = 'launcher' | 'prompt' | 'loading' | 'active' | 'error';

type StoredSelfIdentity = {
  version: 1;
  key: string;
  displayName: string;
  email?: string;
};

const CONNECT_TIMEOUT_MS = 15_000;

export class FinalishLauncher {
  private readonly projectId: string;
  private readonly apiUrl: string;
  private readonly request: typeof fetch;
  private readonly storageKey: string;
  private hostIdentity?: FinalishHostIdentity;
  private selfIdentity?: StoredSelfIdentity;
  private host?: HTMLDivElement;
  private shadow?: ShadowRoot;
  private surface?: HTMLDivElement;
  private widget?: FinalishWidget;
  private client?: FinalishClient;
  private session?: FinalishWidgetSession;
  private activeIdentityKey?: string;
  private lastIdentity?: FinalishWidgetSessionIdentityInput;
  private state: LauncherState = 'launcher';
  private dockSide: FinalishDockSide;
  private error?: string;
  private connectionRevision = 0;
  private destroyed = false;

  constructor(options: FinalishLauncherOptions) {
    this.projectId = options.projectId.trim();
    this.apiUrl = normalizeHttpUrl(options.apiUrl);
    normalizeHttpUrl(options.appUrl);
    this.request = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.storageKey = `finalish.widget.identity.v1.${this.projectId}`;
    this.dockSide = options.dockSide ?? 'right';
    this.hostIdentity = options.identity
      ? normalizeHostIdentity(options.identity)
      : undefined;
    this.selfIdentity = this.readSelfIdentity();
  }

  async mount(): Promise<void> {
    if (this.host || this.destroyed || !this.projectId) return;
    const config = await this.loadConfig();
    if (this.destroyed || !config.enabled) return;
    this.createSurface();
    this.render();
  }

  async open(): Promise<void> {
    if (this.destroyed || !this.host || this.state === 'loading') return;
    const identity = this.currentIdentity();
    if (!identity) {
      this.state = 'prompt';
      this.error = undefined;
      this.render();
      return;
    }
    await this.connect(identity);
  }

  close(): void {
    if (this.destroyed) return;
    this.connectionRevision += 1;
    this.widget?.destroy();
    this.widget = undefined;
    this.state = 'launcher';
    this.error = undefined;
    this.render();
  }

  identify(identity: FinalishHostIdentity | null): void {
    const next = identity ? normalizeHostIdentity(identity) : undefined;
    if (identityKey(next) === identityKey(this.hostIdentity)) return;

    const reopen = this.state === 'active';
    this.connectionRevision += 1;
    this.widget?.destroy();
    this.widget = undefined;
    this.client = undefined;
    this.session = undefined;
    this.activeIdentityKey = undefined;
    this.hostIdentity = next;
    this.state = 'launcher';
    this.error = undefined;
    this.render();

    if (reopen && next) {
      void this.connect(toHostSessionIdentity(next));
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.connectionRevision += 1;
    this.widget?.destroy();
    this.widget = undefined;
    this.client = undefined;
    this.session = undefined;
    this.host?.remove();
    this.host = undefined;
    this.shadow = undefined;
    this.surface = undefined;
  }

  private async loadConfig(): Promise<WidgetConfig> {
    const endpoint = new URL(
      `/api/v1/widget-config/${encodeURIComponent(this.projectId)}`,
      this.apiUrl,
    );
    endpoint.searchParams.set('url', location.href);
    try {
      const response = await this.request(endpoint, {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return { enabled: false };
      const value = await response.json() as Partial<WidgetConfig>;
      return { enabled: value.enabled === true };
    } catch {
      return { enabled: false };
    }
  }

  private createSurface(): void {
    this.host = document.createElement('div');
    this.host.dataset.finalishOverlay = 'true';
    this.host.dataset.dockSide = this.dockSide;
    this.host.style.cssText =
      'position:fixed;z-index:2147482999;pointer-events:none;';
    this.shadow = this.host.attachShadow({ mode: 'open' });
    this.shadow.innerHTML = `<style>${LAUNCHER_STYLES}</style><div class="surface"></div>`;
    this.surface = this.shadow.querySelector<HTMLDivElement>('.surface') ?? undefined;
    document.body.append(this.host);
  }

  private render(): void {
    if (!this.host || !this.surface) return;
    this.host.style.display = this.state === 'active' ? 'none' : 'block';
    if (this.state === 'active') {
      this.surface.replaceChildren();
      return;
    }

    if (this.state === 'prompt') {
      this.renderPrompt();
      return;
    }
    if (this.state === 'loading') {
      this.surface.innerHTML = `<div class="stack">
        <div class="connection-status" role="status" aria-live="polite">
          <span class="spinner" aria-hidden="true"></span>Подключаем Finalish…
        </div>
        ${launcherButton(true, true)}
      </div>`;
      return;
    }
    if (this.state === 'error') {
      this.renderError();
      return;
    }

    this.surface.innerHTML = launcherButton(false, false);
    this.surface.querySelector<HTMLButtonElement>('[data-launch]')?.addEventListener(
      'click',
      () => void this.open(),
    );
  }

  private renderPrompt(): void {
    if (!this.surface) return;
    this.surface.innerHTML = `<div class="stack">
      <form class="identity-panel" aria-label="Представьтесь, чтобы комментировать">
        <div class="panel-heading">Как вас зовут?</div>
        <p>Имя будет видно рядом с комментариями.</p>
        <label for="finalish-display-name">Имя</label>
        <input id="finalish-display-name" name="displayName" type="text"
          autocomplete="name" minlength="2" maxlength="120" required />
        <label for="finalish-email">Email <span>необязательно</span></label>
        <input id="finalish-email" name="email" type="email"
          autocomplete="email" maxlength="254" />
        <div class="panel-actions">
          <button type="button" class="secondary" data-cancel>Отмена</button>
          <button type="submit" class="primary">Продолжить</button>
        </div>
      </form>
      ${launcherButton(false, true)}
    </div>`;
    const form = this.surface.querySelector<HTMLFormElement>('form');
    const name = form?.elements.namedItem('displayName') as HTMLInputElement | null;
    const email = form?.elements.namedItem('email') as HTMLInputElement | null;
    name?.addEventListener('input', () => name.setCustomValidity(''));
    email?.addEventListener('input', () => email.setCustomValidity(''));
    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      if (name && name.value.trim().length < 2) {
        name.setCustomValidity('Введите имя — минимум 2 символа.');
        name.reportValidity();
        return;
      }
      if (email) email.value = email.value.trim();
      if (email?.value && !isValidEmail(email.value)) {
        email.setCustomValidity('Введите корректный email или оставьте поле пустым.');
        email.reportValidity();
        return;
      }
      if (!form.reportValidity()) return;
      const next = createSelfIdentity(name?.value ?? '', email?.value ?? '');
      this.selfIdentity = next;
      this.writeSelfIdentity(next);
      void this.connect(toSelfSessionIdentity(next));
    });
    this.surface.querySelector<HTMLButtonElement>('[data-cancel]')?.addEventListener(
      'click',
      () => {
        this.state = 'launcher';
        this.render();
      },
    );
    this.surface.querySelector<HTMLButtonElement>('[data-launch]')?.addEventListener(
      'click',
      () => {
        this.state = 'launcher';
        this.render();
      },
    );
    window.requestAnimationFrame(() => name?.focus());
  }

  private renderError(): void {
    if (!this.surface) return;
    this.surface.innerHTML = `<div class="stack">
      <div class="error-panel" role="alert">
        <div class="panel-heading">Не удалось подключиться</div>
        <p data-error></p>
        <div class="panel-actions">
          <button type="button" class="secondary" data-dismiss>Закрыть</button>
          <button type="button" class="primary" data-retry>Повторить</button>
        </div>
        <button type="button" class="change-identity" data-change hidden>Изменить имя</button>
      </div>
      ${launcherButton(false, true)}
    </div>`;
    const error = this.surface.querySelector<HTMLElement>('[data-error]');
    if (error) error.textContent = this.error ?? 'Попробуйте ещё раз.';
    const change = this.surface.querySelector<HTMLButtonElement>('[data-change]');
    if (change && this.lastIdentity?.source === 'self') change.hidden = false;
    this.surface.querySelector<HTMLButtonElement>('[data-retry]')?.addEventListener(
      'click',
      () => {
        if (this.lastIdentity) void this.connect(this.lastIdentity);
        else void this.open();
      },
    );
    this.surface.querySelector<HTMLButtonElement>('[data-dismiss]')?.addEventListener(
      'click',
      () => {
        this.state = 'launcher';
        this.error = undefined;
        this.render();
      },
    );
    this.surface.querySelector<HTMLButtonElement>('[data-launch]')?.addEventListener(
      'click',
      () => {
        this.state = 'launcher';
        this.error = undefined;
        this.render();
      },
    );
    change?.addEventListener('click', () => {
      this.selfIdentity = undefined;
      this.removeSelfIdentity();
      this.state = 'prompt';
      this.error = undefined;
      this.render();
    });
  }

  private async connect(identity: FinalishWidgetSessionIdentityInput): Promise<void> {
    const revision = ++this.connectionRevision;
    this.lastIdentity = identity;
    this.state = 'loading';
    this.error = undefined;
    this.render();

    try {
      if (
        this.client &&
        this.session &&
        this.activeIdentityKey === sessionIdentityKey(identity) &&
        sessionIsReusable(this.session)
      ) {
        await this.activate(this.client, revision);
        return;
      }

      const client = new FinalishClient({
        apiUrl: this.apiUrl,
        fetch: this.request,
        sessionKind: 'widget',
      });
      const session = await withTimeout(
        client.exchangeWidgetSession({
          projectId: this.projectId,
          targetUrl: location.href,
          identity,
        }),
        CONNECT_TIMEOUT_MS,
      );
      validateSession(session, this.projectId, identity.source);
      if (this.destroyed || revision !== this.connectionRevision) return;
      this.client = client;
      this.session = session;
      this.activeIdentityKey = sessionIdentityKey(identity);
      await this.activate(client, revision);
    } catch (error) {
      if (this.destroyed || revision !== this.connectionRevision) return;
      this.widget?.destroy();
      this.widget = undefined;
      this.client = undefined;
      this.session = undefined;
      this.activeIdentityKey = undefined;
      this.state = 'error';
      this.error = errorMessage(error);
      this.render();
    }
  }

  private async activate(client: FinalishClient, revision: number): Promise<void> {
    const widget = new FinalishWidget({
      client,
      dockSide: this.dockSide,
      startHidden: true,
      onClose: () => this.close(),
      onSessionExpired: () => this.handleSessionExpired(),
      onDockSideChange: (side) => {
        this.dockSide = side;
        if (this.host) this.host.dataset.dockSide = side;
      },
    });
    this.widget?.destroy();
    this.widget = widget;
    await widget.mount();
    if (this.destroyed || revision !== this.connectionRevision) {
      widget.destroy();
      return;
    }
    this.state = 'active';
    this.render();
    widget.show();
  }

  private handleSessionExpired(): void {
    this.connectionRevision += 1;
    this.widget?.destroy();
    this.widget = undefined;
    this.client = undefined;
    this.session = undefined;
    this.activeIdentityKey = undefined;
    this.state = 'error';
    this.error = 'Сессия истекла. Подключитесь снова.';
    this.render();
  }

  private currentIdentity(): FinalishWidgetSessionIdentityInput | undefined {
    if (this.hostIdentity) return toHostSessionIdentity(this.hostIdentity);
    if (this.selfIdentity) return toSelfSessionIdentity(this.selfIdentity);
    return undefined;
  }

  private readSelfIdentity(): StoredSelfIdentity | undefined {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return undefined;
      const value = JSON.parse(raw) as Partial<StoredSelfIdentity>;
      if (
        value.version !== 1 ||
        typeof value.key !== 'string' ||
        value.key.length < 8 ||
        typeof value.displayName !== 'string' ||
        value.displayName.trim().length < 2
      ) {
        return undefined;
      }
      return {
        version: 1,
        key: value.key,
        displayName: value.displayName.trim().slice(0, 120),
        ...(typeof value.email === 'string' && value.email.trim()
          ? { email: value.email.trim().slice(0, 254) }
          : {}),
      };
    } catch {
      return undefined;
    }
  }

  private writeSelfIdentity(identity: StoredSelfIdentity): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(identity));
    } catch {
      // Storage may be disabled. Keep the identity in memory for this page.
    }
  }

  private removeSelfIdentity(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // Storage may be disabled; the in-memory value is already cleared.
    }
  }
}

function launcherButton(disabled: boolean, expanded: boolean): string {
  return `<button type="button" class="launcher-button" data-launch
    aria-label="${disabled ? 'Finalish подключается' : 'Открыть Finalish'}"
    title="${disabled ? 'Finalish подключается' : 'Открыть Finalish'}"
    aria-expanded="${expanded}"${disabled ? ' disabled' : ''}>
    ${LOGO}
  </button>`;
}

function normalizeHostIdentity(identity: FinalishHostIdentity): FinalishHostIdentity {
  const id = identity.id.trim();
  const name = identity.name.trim();
  if (!id || id.length > 200 || name.length < 2 || name.length > 120) {
    throw new TypeError('Finalish host identity requires a valid id and name');
  }
  const email = identity.email?.trim();
  if (email && !isValidEmail(email)) {
    throw new TypeError('Finalish host identity email is invalid');
  }
  return { id, name, ...(email ? { email: email.slice(0, 254) } : {}) };
}

function createSelfIdentity(name: string, email: string): StoredSelfIdentity {
  const displayName = name.trim();
  const normalizedEmail = email.trim();
  return {
    version: 1,
    key: createInstallationKey(),
    displayName,
    ...(normalizedEmail ? { email: normalizedEmail } : {}),
  };
}

function toHostSessionIdentity(
  identity: FinalishHostIdentity,
): FinalishWidgetSessionIdentityInput {
  return {
    source: 'host',
    key: identity.id,
    displayName: identity.name,
    ...(identity.email ? { email: identity.email } : {}),
  };
}

function toSelfSessionIdentity(
  identity: StoredSelfIdentity,
): FinalishWidgetSessionIdentityInput {
  return {
    source: 'self',
    key: identity.key,
    displayName: identity.displayName,
    ...(identity.email ? { email: identity.email } : {}),
  };
}

function sessionIdentityKey(identity: FinalishWidgetSessionIdentityInput): string {
  return `${identity.source}\u0000${identity.key}\u0000${identity.displayName}\u0000${identity.email ?? ''}`;
}

function identityKey(identity: FinalishHostIdentity | undefined): string {
  return identity
    ? `${identity.id}\u0000${identity.name}\u0000${identity.email ?? ''}`
    : '';
}

function createInstallationKey(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
}

function isValidEmail(value: string): boolean {
  return value.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function validateSession(
  session: FinalishWidgetSession,
  projectId: string,
  identitySource: 'self' | 'host',
): void {
  if (session.projectId !== projectId) {
    throw new FinalishApiError(403, 'widget_project_mismatch', 'Сессия выдана для другого проекта.');
  }
  let targetOrigin = '';
  try {
    targetOrigin = new URL(session.targetOrigin).origin;
  } catch {
    // Keep the generic mismatch below.
  }
  if (targetOrigin !== location.origin) {
    throw new FinalishApiError(403, 'widget_origin_mismatch', 'Сессия выдана для другого сайта.');
  }
  if (session.identitySource !== identitySource) {
    throw new FinalishApiError(403, 'widget_identity_mismatch', 'Сессия выдана для другой учётной записи.');
  }
  if (!session.sessionToken || !sessionIsReusable(session)) {
    throw new FinalishApiError(401, 'widget_session_invalid', 'Получена недействительная сессия.');
  }
}

function sessionIsReusable(session: FinalishWidgetSession): boolean {
  const expiresAt = new Date(session.expiresAt).getTime();
  return Number.isFinite(expiresAt) && expiresAt > Date.now() + 5_000;
}

function normalizeHttpUrl(value: string): string {
  const url = new URL(value);
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    throw new Error('Finalish URL must use HTTP or HTTPS');
  }
  return url.toString().replace(/\/+$/, '');
}

function errorMessage(error: unknown): string {
  if (error instanceof FinalishApiError || error instanceof Error) {
    return error.message;
  }
  return 'Не удалось подключить Finalish. Попробуйте ещё раз.';
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      reject(new Error('Сервер Finalish не ответил. Попробуйте ещё раз.'));
    }, timeoutMs);
    promise.then(
      (value) => {
        window.clearTimeout(timeout);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeout);
        reject(error);
      },
    );
  });
}

const LOGO = `<svg viewBox="0 0 64 64" fill="none" aria-hidden="true" focusable="false">
  <g transform="translate(14 6)">
    <path d="M0 52V16C0 7.163 7.163 0 16 0H25V8H16C11.582 8 8 11.582 8 16H27L20 24H8V52H0Z" fill="currentColor"/>
    <path d="M29 16H36V52H28V24H22Z" fill="#211A37"/>
    <path d="M28 0H36V8H28Z" fill="#211A37"/>
  </g>
</svg>`;

const LAUNCHER_STYLES = `
  :host{right:max(12px,env(safe-area-inset-right));bottom:max(12px,env(safe-area-inset-bottom));
    --primary:#5417ee;--primary-dark:#4513c4;--primary-soft:#f3efff;--ink:#211a37;
    --text:#352e49;--muted:#766f82;--surface-hover:#f6f5f8;--border:#e2e1ea;
    --border-strong:#cbc8d6;font-family:"Onest",ui-sans-serif,system-ui,sans-serif;
    color:var(--text);font-synthesis:none}:host([data-dock-side="left"]){right:auto;
    left:max(12px,env(safe-area-inset-left))}
  *{box-sizing:border-box}.surface,.stack{display:grid;justify-items:end;gap:8px;pointer-events:none}
  :host([data-dock-side="left"]) .surface,:host([data-dock-side="left"]) .stack{justify-items:start}
  button,input{font:inherit}.launcher-button{width:40px;height:40px;display:grid;place-items:center;padding:0;
    border:1px solid var(--border);border-radius:9px;color:var(--primary);background:#fff;
    box-shadow:0 2px 8px rgb(33 26 55 / 14%);pointer-events:auto;cursor:pointer;
    transition:color 120ms ease,background-color 120ms ease,border-color 120ms ease}
  .launcher-button:hover{border-color:var(--border-strong);background:var(--primary-soft)}
  .launcher-button:active{background:#eae3ff}.launcher-button:disabled{cursor:wait;opacity:.68}
  .launcher-button:focus-visible,button:focus-visible,input:focus-visible{outline:2px solid var(--primary);outline-offset:2px}
  .launcher-button svg{width:25px;height:25px;display:block}.identity-panel,.error-panel,.connection-status{
    width:280px;max-width:calc(100vw - 24px);max-height:calc(100dvh - 60px);overflow:auto;padding:14px;
    border:1px solid var(--border);border-radius:10px;background:#fff;box-shadow:0 3px 8px rgb(33 26 55 / 15%);
    pointer-events:auto}.panel-heading{color:var(--ink);font:600 14px/20px "Onest",ui-sans-serif,system-ui,sans-serif}
  p{margin:3px 0 12px;color:var(--muted);font:400 12px/18px "Onest",ui-sans-serif,system-ui,sans-serif}
  label{display:block;margin:10px 0 5px;color:var(--text);font:500 12px/18px "Onest",ui-sans-serif,system-ui,sans-serif}
  label span{color:var(--muted);font-weight:400}input{width:100%;height:36px;padding:0 9px;
    border:1px solid var(--border-strong);border-radius:7px;background:#fff;color:var(--ink);font-size:13px}
  input:hover{border-color:#aaa5b7}input:focus{border-color:var(--primary);outline:2px solid rgb(84 23 238 / 15%)}
  .panel-actions{display:flex;justify-content:flex-end;gap:7px;margin-top:14px}.panel-actions button{
    height:32px;padding:0 10px;border-radius:7px;font-size:12px;font-weight:600;cursor:pointer}
  .secondary{border:1px solid var(--border-strong);background:#fff;color:var(--text)}
  .secondary:hover{background:var(--surface-hover)}.primary{border:1px solid var(--primary);background:var(--primary);color:#fff}
  .primary:hover{background:var(--primary-dark)}.connection-status{display:flex;align-items:center;gap:8px;width:auto;
    padding:8px 10px;color:var(--text);font-size:12px}.spinner{width:13px;height:13px;border:2px solid #d8cef6;
    border-top-color:var(--primary);border-radius:50%;animation:spin 700ms linear infinite}.error-panel p{margin-bottom:0}
  .change-identity{margin-top:9px;padding:0;border:0;background:transparent;color:var(--primary);font-size:12px;
    cursor:pointer}.change-identity:hover{text-decoration:underline}@keyframes spin{to{rotate:360deg}}
  @media(max-width:560px){:host{right:max(10px,env(safe-area-inset-right));
      bottom:max(10px,env(safe-area-inset-bottom))}:host([data-dock-side="left"]){
      left:max(10px,env(safe-area-inset-left))}input{font-size:16px}.identity-panel,.error-panel{max-width:calc(100vw - 20px)}}
  @media(max-height:420px){.identity-panel,.error-panel{max-height:calc(100dvh - 52px)}}
  @media(prefers-reduced-motion:reduce){*{transition:none!important}.spinner{animation-duration:1400ms}}
  @media(forced-colors:active){.launcher-button,.identity-panel,.error-panel,.connection-status{border:1px solid ButtonText}}
`;
